import React, { useEffect, useRef } from 'react';

// Accessible modal wrapper with ESC-to-close + focus trap + restore-focus
export default function Modal({ onClose, children, ariaLabel, placement = 'center' }) {
  const containerRef = useRef(null);
  const lastActiveRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const isBottom = placement === 'bottom';

  // swipe-to-close handling for bottom modals
  const touchStartRef = useRef(null);
  const handleTouchStart = (e) => {
    if (!isBottom) return;
    touchStartRef.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e) => {
    if (!isBottom || touchStartRef.current === null) return;
    const currentY = e.touches[0].clientY;
    const dy = currentY - touchStartRef.current;
    // if user drags down a bit, close the modal
    if (dy > 50) {
      onCloseRef.current?.();
      touchStartRef.current = null;
    }
  };
  const handleTouchEnd = () => {
    touchStartRef.current = null;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // save previously focused element
    lastActiveRef.current = document.activeElement;

    // focus first focusable element inside the modal (or the container)
    const focusableSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(container.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);

    const focusFirst = () => {
      const focusables = getFocusable();
      if (focusables.length) focusables[0].focus();
      else container.focus();
    };

    // small timeout so children refs (inputs) mount first
    const t = setTimeout(focusFirst, 0);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key === 'Tab') {
        const focusables = getFocusable();
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }

        const currentIndex = focusables.indexOf(document.activeElement);
        if (e.shiftKey) {
          // Shift+Tab
          if (currentIndex <= 0) {
            focusables[focusables.length - 1].focus();
            e.preventDefault();
          }
        } else {
          // Tab
          if (currentIndex === focusables.length - 1) {
            focusables[0].focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKeyDown, true);
      // restore focus
      try {
        if (lastActiveRef.current && typeof lastActiveRef.current.focus === 'function') {
          lastActiveRef.current.focus();
        }
      } catch (err) {
        // ignore
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`fixed inset-0 z-50 flex ${isBottom ? 'items-end justify-center' : 'items-center justify-center'}`}
      onClick={(e) => {
        // only close when backdrop itself is clicked (not inner content)
        if (e.target === e.currentTarget) {
          onCloseRef.current?.();
        }
      }}
    >
      {/* backdrop: darker + slight blur for center, lighter for bottom */}
      <div
        className={`absolute inset-0 ${isBottom ? 'bg-black/20' : 'bg-black/60 backdrop-blur-sm'}`}
      />

      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`z-50 outline-none ${isBottom ? 'rounded-t-2xl max-w-md w-full p-6 space-y-4 animate-in slide-in-from-bottom-4' : 'rounded-2xl shadow-lg'}`}
        style={{ maxHeight: '90vh', overflowY: 'auto', background: 'var(--c-surface, #18181C)', ...(isBottom ? { height: 'calc(100dvh - 48px)' } : {}) }}
      >
        {children}
      </div>
    </div>
  );
}
